/** Os campos e a janela da Aplicação de Exames (#606). */
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { InputAdornment, Link, TextField, Typography } from '@mui/material';

import { pode } from '../../authorization/pode';
import { useControle } from '../../authorization/useVeredito';
import type { AvisoDosItensProps, JanelaDeDetalheProps } from '../../components/applications/tipos';
import { ExamApplicationModal } from '../../components/exam-applications/ExamApplicationModal';
import { examApplicationService } from '../../services/examApplicationService';
import type { ApplicationData, ExamWithApplication } from '../../types/examApplication';
import { OBRIGATORIO, OPCIONAL } from './textosDoExame';

interface SeletorDeObrigatorioProps {
  valor: boolean | null;
  rotulo: string;
  aoMudar: (novo: boolean) => void;
}

export function SeletorDeObrigatorio({ valor, rotulo, aoMudar }: SeletorDeObrigatorioProps) {
  const atual = valor === null ? '' : valor ? OBRIGATORIO : OPCIONAL;
  return (
    <TextField
      select
      size="small"
      value={atual}
      onChange={(evento) => aoMudar(evento.target.value === OBRIGATORIO)}
      SelectProps={{ native: true }}
      inputProps={{ 'aria-label': rotulo }}
      sx={{ minWidth: 140 }}
    >
      {valor === null && (
        <option value="" disabled>
          Escolha
        </option>
      )}
      <option value={OBRIGATORIO}>Obrigatório</option>
      <option value={OPCIONAL}>Opcional</option>
    </TextField>
  );
}

interface CampoDePeriodicidadeProps {
  valor: number | null;
  rotulo: string;
  aoMudar: (novo: number | null) => void;
}

/**
 * Meses, vazio = sem periodicidade. Grava ao sair do campo (ou com Enter), não a cada tecla:
 * na linha, cada gravação é um pedido ao servidor.
 */
export function CampoDePeriodicidade(props: CampoDePeriodicidadeProps) {
  // A chave recomeça o texto quando o valor gravado muda de fora (Desfazer, lote, releitura).
  return <TextoDaPeriodicidade key={String(props.valor)} {...props} />;
}

function TextoDaPeriodicidade({ valor, rotulo, aoMudar }: CampoDePeriodicidadeProps) {
  const [texto, setTexto] = useState(valor === null ? '' : String(valor));

  const confirmar = () => {
    const meses = Number(texto);
    const novo = texto.trim() !== '' && Number.isInteger(meses) && meses > 0 ? meses : null;
    if (novo !== valor) aoMudar(novo);
    else setTexto(valor === null ? '' : String(valor));
  };

  return (
    <TextField
      type="number"
      size="small"
      value={texto}
      placeholder="Sem"
      onChange={(evento) => setTexto(evento.target.value)}
      onBlur={confirmar}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter') confirmar();
      }}
      inputProps={{ 'aria-label': rotulo, min: 1, max: 120 }}
      InputProps={{ endAdornment: <InputAdornment position="end">meses</InputAdornment> }}
      sx={{ width: 150 }}
    />
  );
}


export function DetalheDoExame({ aberta, aplicacao, destino, caminho, aoFechar, aoSalvar }: JanelaDeDetalheProps<ExamWithApplication>) {
  const salvar = async (dados: ApplicationData) => {
    await examApplicationService.updateForTarget(destino.nivel, destino.id, Number(aplicacao.examId), dados);
    aoSalvar();
  };
  return (
    <ExamApplicationModal
      open={aberta}
      onClose={aoFechar}
      onConfirm={salvar}
      exam={aplicacao}
      mode="edit"
      destino={destino}
      caminho={caminho}
    />
  );
}

/**
 * Vencimento é de cada funcionário: quem mostra vencidos e a vencer é a Convocação (#567). O
 * atalho leva à da empresa do destino; sem acesso à Convocação, fica só o texto.
 */
export function AvisoDaConvocacao({ empresaId }: AvisoDosItensProps) {
  const abrirConvocacao = useControle('/exam-summons', pode('exam_summons.read'), 'Abrir Convocação de Exames');
  const endereco =
    empresaId !== null ? `/exam-summons?status=overdue&companyId=${String(empresaId)}` : '/exam-summons?status=overdue';
  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Vencidos e a vencer, funcionário por funcionário, ficam na Convocação de Exames.
      {!abrirConvocacao.disabled && (
        <>
          {' '}
          <Link component={RouterLink} to={endereco}>
            Abrir Convocação de Exames
          </Link>
        </>
      )}
    </Typography>
  );
}
